import { useEffect, type JSX } from 'react';
import { useLocation } from 'react-router';
import { usePostHog } from '@posthog/react';
import {
  Button,
  Card,
  CheckIcon,
  CommentIcon,
  StatusIndicator
} from '@viniciomendesr/rayrovisk-design-system';
import { AccessDeniedError, type AccessState } from '../api/client';
import { useMe } from '../api/queries';
import { OFFERS, brl, checkout, type Product } from '../screens/salesContent';

const COPY: Record<
  AccessState,
  { status: 'idle' | 'live' | 'review' | 'failed'; title: string; body: string }
> = {
  granted: {
    status: 'live',
    title: 'Acesso liberado.',
    body: 'Se você está vendo esta tela, algo ficou no meio do caminho. Recarregue a página.'
  },
  not_owned: {
    status: 'idle',
    title: 'Este produto não está no seu acesso.',
    body: 'Você ainda não comprou este produto. Pode liberar o acesso agora.'
  },
  revoked: {
    status: 'failed',
    title: 'Este acesso foi encerrado pelo reembolso.',
    body: 'A compra foi reembolsada, e o acesso fecha junto. Se isso não bate com o que você lembra, fale com a gente: a gente olha a transação e devolve o acesso na mão.'
  },
  in_review: {
    status: 'review',
    title: 'Seu pedido de cancelamento está em análise.',
    body: 'Você continua usando tudo enquanto isso. Nada foi cortado, e nada muda até a plataforma processar o pedido.'
  }
};

interface AccessOffer {
  product: Product;
  price: number;
  compareAt?: number;
  installment?: string;
  body: string;
  benefits: readonly string[];
  checkoutUrl: string | null;
}

const ACCESS_OFFERS: Record<'starter' | 'generator', AccessOffer> = {
  starter: {
    product: 'PROMPT_PACK_STARTER',
    price: OFFERS.starter.price,
    body: 'Você ainda não comprou este produto. Pode liberar o acesso agora.',
    benefits: [
      '20 prompts prontos e organizados por objetivo',
      'Acesso imediato à Biblioteca de Prompts',
      'Pagamento único, sem mensalidade'
    ],
    checkoutUrl: checkout('PROMPT_PACK_STARTER')
  },
  generator: {
    product: 'PROMPT_GENERATOR',
    price: OFFERS.generator.price,
    body: 'Você ainda não comprou este produto. Pode liberar o acesso agora.',
    benefits: [
      '100 roteiros na conta, e eles não expiram',
      'Cada roteiro sai com o perfil que você já preencheu, na sua voz',
      'O acesso abre na hora do pagamento, sem mensalidade'
    ],
    checkoutUrl: checkout('PROMPT_GENERATOR')
  }
};

/** The route says which product the person tried to use. */
function offerFor(pathname: string): AccessOffer {
  if (
    pathname === '/gerador' ||
    pathname.startsWith('/gerador/montar') ||
    pathname.startsWith('/gerador/ia') ||
    pathname.startsWith('/roteiros')
  ) {
    return ACCESS_OFFERS.generator;
  }

  return ACCESS_OFFERS.starter;
}

/**
 * RS-15: four states, four screens, none of them a generic 403. "Você nunca
 * comprou isto" and "seu reembolso foi processado" are different facts, and the
 * person on the other side deserves to know which one she is looking at.
 */
export function AccessStateScreen({ state }: { state: AccessState }): JSX.Element {
  const copy = COPY[state];
  const posthog = usePostHog();
  const me = useMe();
  const { pathname } = useLocation();
  const offer = state === 'not_owned' && me.data ? offerFor(pathname) : null;

  /**
   * Every render of this screen is somebody asking for a product she does not
   * hold, so counting it is counting demand: which route the wall stands on
   * says what the next step of the ladder is worth building, and `revoked`
   * separated from `not_owned` says whether the wall is a price or a refund.
   *
   * The route stands in for the product because that is what she asked for.
   * `/roteiros` is the script generator, `/gerador/montar` is the assembler,
   * and no identifier beyond the path leaves the browser.
   */
  useEffect(() => {
    posthog?.capture('access_denied', { state, path: pathname });
  }, [state, pathname, posthog]);

  return (
    <div className="rvk-access-state">
      <Card layer="functional" elevation="raised" padding="lg">
        <div className="rvk-access-state-content">
          <StatusIndicator status={copy.status} label={copy.title} />
          <p
            style={{
              margin: 0,
              font: '400 14.5px/1.6 var(--rvk-font-functional)',
              color: 'var(--rvk-text-secondary)',
              textWrap: 'pretty'
            }}
          >
            {offer?.body ?? copy.body}
          </p>

          {offer && (
            <>
              <div className="rvk-access-offer">
                <div className="rvk-access-price" aria-label={`${brl(offer.price)} à vista`}>
                  {offer.compareAt && <del>{brl(offer.compareAt)}</del>}
                  <strong>{brl(offer.price)}</strong>
                  <span>
                    à vista{offer.installment ? `, ${offer.installment}` : ''} · pagamento único
                  </span>
                </div>

                <ul className="rvk-access-benefits">
                  {offer.benefits.map((benefit) => (
                    <li key={benefit}>
                      <CheckIcon size={14} />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rvk-access-actions">
                {offer.checkoutUrl ? (
                  <a
                    href={offer.checkoutUrl}
                    onClick={() =>
                      posthog?.capture('checkout_initiated', {
                        product: offer.product,
                        price: offer.price,
                        surface: 'access_state'
                      })
                    }
                  >
                    <Button variant="primary" size="sm">
                      Comprar por {brl(offer.price)} e liberar agora
                    </Button>
                  </a>
                ) : (
                  <a href="mailto:acesso@roteirovisk.com?subject=Liberar%20o%20acesso">
                    <Button variant="primary" size="sm">
                      Solicitar o acesso por {brl(offer.price)}
                    </Button>
                  </a>
                )}
                <a href="mailto:acesso@roteirovisk.com">
                  <Button variant="ghost" size="sm" iconStart={<CommentIcon size={14} />}>
                    Falar com o suporte
                  </Button>
                </a>
              </div>
            </>
          )}

          {!offer && (
            <a href="mailto:acesso@roteirovisk.com">
              <Button variant="ghost" size="sm" iconStart={<CommentIcon size={14} />}>
                Falar com o suporte
              </Button>
            </a>
          )}
        </div>
      </Card>
    </div>
  );
}

/**
 * The one place a screen turns a failed query into an access screen. Anything
 * that is not an access refusal is re-thrown rather than swallowed, so a real
 * outage never renders as "you do not own this".
 */
export function accessStateOf(error: unknown): AccessState | null {
  return error instanceof AccessDeniedError ? error.state : null;
}
